import { queryOptions } from '@tanstack/react-query';

import { fetchBackend } from '../../../apiClient';

export type AwsCredentialsToCreate = Omit<
  AwsCredentials,
  'accountId' | 'status'
>;

type AwsCredentialsStatus = 'active' | 'inactive' | 'error';

export type AwsCredentials = {
  accountName: string;
  accessKey: string;
  secretKey: string;
  accountId: string;
  status: AwsCredentialsStatus;
};

export type AwsCredentialsForList = {
  name: string;
  id: string;
  status: AwsCredentialsStatus;
};

export const awsCredentialsQueries = {
  all: () => ['aws-credentials'],
  byProject: (projectId: string) => [...awsCredentialsQueries.all(), projectId],
  list: (projectId: string) =>
    queryOptions({
      queryKey: [...awsCredentialsQueries.byProject(projectId), 'list'],
      queryFn: async () => {
        const awsCredentials = await listAwsAccounts(projectId);

        return awsCredentials;
      },
    }),
  details: (projectId: string) => [
    ...awsCredentialsQueries.byProject(projectId),
    'details',
  ],
  detail: (projectId: string, accountId: string) =>
    queryOptions({
      queryKey: [...awsCredentialsQueries.details(projectId), accountId],
      queryFn: async () => {
        const awsCredentials = await getAwsCredentials(projectId, accountId);

        return awsCredentials;
      },
    }),
} as const;

export const upsertAwsCredentials = async (
  projectId: string,
  payload: AwsCredentialsToCreate,
  accountId?: string,
): Promise<AwsCredentialsToCreate> => {
  if (accountId !== undefined) {
    const updateResponse = await fetchBackend(
      `/aws-credentials/${projectId}/${accountId}`,
      {
        method: 'PUT',
        body: JSON.stringify(payload),
      },
    );

    if (!updateResponse.ok) {
      throw new Error(`Error: ${updateResponse.statusText}`);
    }

    return payload;
  }

  const createResponse = await fetchBackend(`/aws-credentials/${projectId}`, {
    method: 'POST',
    body: JSON.stringify(payload),
  });

  if (!createResponse.ok) {
    throw new Error(`Error: ${createResponse.statusText}`);
  }

  return payload;
};

export const getAwsCredentials = async (
  projectId: string,
  accountId: string,
): Promise<AwsCredentials> => {
  const getResponse = await fetchBackend(
    `/aws-credentials/${projectId}/${accountId}`,
    {
      method: 'GET',
    },
  );

  if (!getResponse.ok) {
    throw new Error(`Error: ${getResponse.statusText}`);
  }

  const data = (await getResponse.json()) as {
    awsCredentials: AwsCredentials;
  };

  return data.awsCredentials;
};

export const listAwsAccounts = async (
  projectId: string,
): Promise<AwsCredentialsForList[]> => {
  const getResponse = await fetchBackend(`/aws-credentials/${projectId}`, {
    method: 'GET',
  });

  if (!getResponse.ok) {
    throw new Error(`Error: ${getResponse.statusText}`);
  }

  const data = (await getResponse.json()) as {
    awsAccounts: AwsCredentialsForList[];
  };

  return data.awsAccounts;
};

export const deleteAwsCredentials = async (
  projectId: string,
  accountId: string,
): Promise<void> => {
  const deleteResponse = await fetchBackend(
    `/aws-credentials/${projectId}/${accountId}`,
    {
      method: 'DELETE',
    },
  );

  if (!deleteResponse.ok) {
    throw new Error(`Error: ${deleteResponse.statusText}`);
  }
};
